import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { AdmissionStatus } from './AdmissionStatus';
import { CERTIFICATION_CONSEQUENCE } from '../../admission/labels';
import {
  acceptedMandatoryStatements,
  buildCertificationRequest,
  canSubmitCertification,
  certificationContextKey,
  certificationSnapshotComplete,
  mandatoryStatementIds,
} from '../../admission/certificationContract';
import {
  authoritativeCandidateCount,
  canCertifyAdmission,
  evidenceReferenceCount,
  supportingDocumentCount,
} from '../../admission/selectors';
import { createPackageCertification, getPackageAdmission } from '../../api/originatorApi';
import { ApiError, formatApiFailure } from '../../api/apiError';

function truncateHash(value) {
  if (!value) return '—';
  const text = String(value);
  if (text.length <= 20) return text;
  return `${text.slice(0, 10)}…${text.slice(-6)}`;
}

export function PackageCertificationModal({
  open,
  onClose,
  onCertified,
  onStale,
  onAdmissionUpdated,
  packageId,
  admission,
  profile,
}) {
  const [accepted, setAccepted] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const statements = mandatoryStatementIds(admission);
  const contextKey = certificationContextKey(admission);
  const ready = canSubmitCertification(admission, accepted);
  const snapshotReady = certificationSnapshotComplete(admission);

  useEffect(() => {
    if (!open) return;
    setAccepted({});
    setError('');
    setSaving(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setAccepted({});
  }, [open, contextKey]);

  async function handleCertify() {
    if (!ready || saving) return;
    setSaving(true);
    setError('');
    const acceptedIds = acceptedMandatoryStatements(admission, accepted);
    try {
      const latest = await getPackageAdmission(packageId);
      if (
        certificationContextKey(latest) !== contextKey ||
        !canCertifyAdmission(latest) ||
        !certificationSnapshotComplete(latest)
      ) {
        onAdmissionUpdated?.(latest);
        const stale = new ApiError(
          'Certification context is stale; refresh admission and try again.',
          {
            status: 409,
            code: 'STALE_CERTIFICATION_CONTEXT',
            payload: { row_version: latest?.row_version ?? null },
          }
        );
        onStale?.(stale, latest);
        return;
      }
      const result = await createPackageCertification(
        packageId,
        buildCertificationRequest(latest, acceptedIds)
      );
      onCertified?.(result);
    } catch (requestError) {
      if (requestError?.status === 409 || requestError?.code === 'STALE_CERTIFICATION_CONTEXT') {
        let latest = null;
        try {
          latest = await getPackageAdmission(packageId);
          onAdmissionUpdated?.(latest);
        } catch {
          latest = null;
        }
        onStale?.(requestError, latest);
        return;
      }
      setError(formatApiFailure(requestError, 'Unable to certify package.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={saving ? undefined : onClose}
      size="lg"
      title="Package certification"
      description="Confirm the statement identifiers from this admission snapshot. Certification submits this package for Provider review."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="gradient"
            onClick={handleCertify}
            disabled={saving || !ready}
            aria-disabled={saving || !ready}
          >
            {saving ? 'Certifying…' : 'Certify package'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[12px] font-semibold uppercase tracking-wide text-eorig-muted">
            Admission readiness
          </span>
          <AdmissionStatus status={admission?.readiness_status} />
        </div>

        <dl className="grid grid-cols-2 gap-2 text-[13px]">
          <div>
            <dt className="text-[11px] font-semibold uppercase text-eorig-muted">
              Authoritative candidates
            </dt>
            <dd className="font-semibold text-eorig-ink">{authoritativeCandidateCount(admission)}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold uppercase text-eorig-muted">
              Supporting documents
            </dt>
            <dd className="font-semibold text-eorig-ink">{supportingDocumentCount(admission)}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold uppercase text-eorig-muted">Evidence</dt>
            <dd className="font-semibold text-eorig-ink">{evidenceReferenceCount(admission)}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold uppercase text-eorig-muted">Warnings</dt>
            <dd className="font-semibold text-eorig-ink">{admission?.warning_count ?? 0}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold uppercase text-eorig-muted">
              Programme profile version
            </dt>
            <dd className="font-semibold text-eorig-ink">
              {admission?.admission_profile_version ?? profile?.version ?? '—'}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold uppercase text-eorig-muted">
              Attestation version
            </dt>
            <dd className="font-mono text-[12px] text-eorig-ink">
              {admission?.attestation_version || '—'}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold uppercase text-eorig-muted">Policy hash</dt>
            <dd className="font-mono text-[12px] text-eorig-ink" title={admission?.admission_policy_hash || ''}>
              {truncateHash(admission?.admission_policy_hash)}
            </dd>
          </div>
          {admission?.file_manifest_hash ? (
            <div>
              <dt className="text-[11px] font-semibold uppercase text-eorig-muted">
                File manifest hash
              </dt>
              <dd className="font-mono text-[12px]" title={admission.file_manifest_hash}>
                {truncateHash(admission.file_manifest_hash)}
              </dd>
            </div>
          ) : null}
          {admission?.fact_snapshot_hash ? (
            <div>
              <dt className="text-[11px] font-semibold uppercase text-eorig-muted">
                Fact snapshot hash
              </dt>
              <dd className="font-mono text-[12px]" title={admission.fact_snapshot_hash}>
                {truncateHash(admission.fact_snapshot_hash)}
              </dd>
            </div>
          ) : null}
          {admission?.assessment_hash ? (
            <div>
              <dt className="text-[11px] font-semibold uppercase text-eorig-muted">
                Assessment hash
              </dt>
              <dd className="font-mono text-[12px]" title={admission.assessment_hash}>
                {truncateHash(admission.assessment_hash)}
              </dd>
            </div>
          ) : null}
        </dl>

        <p className="rounded-lg bg-eorig-surface px-3 py-2 text-[12px] leading-snug text-eorig-ink">
          {CERTIFICATION_CONSEQUENCE}
        </p>

        {!snapshotReady ? (
          <p className="text-[12px] font-medium text-rose-600">
            This admission snapshot is incomplete. Refresh admission before certifying.
          </p>
        ) : null}

        <fieldset className="space-y-2">
          <legend className="text-[12px] font-semibold text-eorig-ink">Mandatory statements</legend>
          {statements.map((id) => (
            <label key={id} className="flex items-start gap-2 text-[13px] text-eorig-ink">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={Boolean(accepted[id])}
                onChange={(event) =>
                  setAccepted((current) => ({
                    ...current,
                    [id]: event.target.checked,
                  }))
                }
              />
              <span className="font-mono">{id}</span>
            </label>
          ))}
        </fieldset>

        {error ? <p className="text-[12px] font-medium text-rose-600">{error}</p> : null}
      </div>
    </Modal>
  );
}
